import React from 'react';
import { Target } from 'phosphor-react';
import { motion } from 'framer-motion';

const SubcourseOverallCard = ({ level, levelNumber, progress, loading = false, masteredCount = 0, isMastered = false, compact = false }) => {
  // progress is already 1-100
  const progressValue = progress !== null ? Math.min(100, Math.max(0, progress || 0)) : 0;
  const displayLevel = levelNumber !== null ? (levelNumber || 0) : null;
  // changed by Cursor: scale font size down for 2-3 digit numbers to avoid layout shift
  const levelDigits = String(displayLevel ?? '').length;
  const levelFontSize = levelDigits >= 3 ? '20px' : levelDigits === 2 ? '22px' : '28px';

  return (
    <div className={`subcourse-overall-card${isMastered ? ' mastered' : ''} ${isMastered ? 'bg-gradient-to-br from-[#facc15] via-[#eab308] to-[#ca8a04] shadow-[0_4px_16px_rgba(234,179,8,0.4),0_2px_8px_rgba(234,179,8,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]' : 'bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928]'} rounded-2xl max-md:rounded-xl text-white flex flex-col gap-1.5 self-stretch relative overflow-hidden ${compact ? 'w-full min-w-0 flex-shrink-0 h-auto min-h-[100px] rounded-xl p-[10px_12px] gap-1 shadow-[0_2px_8px_rgba(0,0,0,0.08)] border-[1.5px]' : 'p-[12px_16px] max-md:p-[16px_14px] w-[250px] max-md:w-[calc(100%-48px)] max-md:max-w-[calc(100%-48px)] flex-shrink-0 max-md:gap-2 min-h-[130px] max-md:min-h-0 max-md:shadow-[0_2px_8px_rgba(0,0,0,0.08)] max-md:border-[1.5px]'} ${isMastered ? 'max-md:border-[rgba(234,179,8,0.5)]' : 'max-md:border-[rgba(18,118,109,0.2)]'}`}>
      {/* changed by Cursor: show badge ribbon behind entire card including header, centered with level number */}
      {isMastered && (
        <>
          <img
            src="/DashboardAsset/completion-ribbon.png"
            alt="Completion badge"
            className="absolute pointer-events-none z-[1] w-[140px] max-w-[140px] h-auto top-[calc(50%+22px)] max-md:top-[calc(50%+20px)] right-[-22px] max-md:right-[-20px] -translate-y-1/2"
          />
          <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden rounded-[inherit]" aria-hidden>
            <motion.div
              className="absolute top-0 h-full w-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
            />
          </div>
        </>
      )}
      <div className="flex items-center justify-between relative z-[3]">
        <div className="flex items-center gap-[6px] max-md:gap-[5px]">
          <Target size={14} weight="fill" className={isMastered ? 'text-[#78350f]' : ''} />
          <span className={`text-[12px] font-semibold${isMastered ? ' text-[#78350f]' : ''}`}>{level} Level</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 flex-1 relative z-[3] max-md:flex-row max-md:items-start max-md:gap-1">
        <div className="flex flex-col gap-0.5 relative max-md:min-w-[50px]">
          <span className={`text-[11px] max-md:text-[9px] font-semibold tracking-[0.4px] relative z-[2]${isMastered ? ' text-[#78350f]' : ' text-[rgba(255,255,255,0.8)]'}`}>Level</span>
          <div className="flex items-baseline gap-[3px] relative z-[2]">
            {loading ? (
              <div className={`text-[28px] max-md:text-[22px] font-bold leading-none animate-blink${isMastered ? ' text-[#78350f]' : ''}`}>--</div>
            ) : (
              <div className={`font-bold leading-none${isMastered ? ' text-[#78350f]' : ''}`} style={{ fontSize: levelFontSize }}>{displayLevel}</div>
            )}
            {/* changed by Cursor: warna /100 ikut warna level number via CSS */}
            <span className={isMastered ? 'text-[#78350f]' : 'text-[rgba(255,255,255,0.6)]'} style={{ fontSize: '11px', opacity: 0.5, fontWeight: 400 }}>/100</span>
          </div>
        </div>
        {/* changed by Cursor: always show progress bar, even after mastered */}
        <div className="flex flex-col gap-[3px] max-md:flex-1 max-md:gap-0.5">
          <span className={`text-[11px] max-md:text-[10px] font-semibold${isMastered ? ' text-[#78350f]' : ' text-[rgba(255,255,255,0.8)]'}`}>Progress</span>
          {loading ? (
            <>
              <div className="flex items-center gap-1">
                <div className={`flex-1 h-[6px] rounded-full overflow-hidden${isMastered ? ' bg-[rgba(120,53,15,0.15)]' : ' bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(255,255,255,0.08)]'}`}>
                  <div className={`h-full rounded-full animate-blink transition-[width_0.3s_ease]${isMastered ? ' bg-[#78350f]' : ' bg-[#fcb319]'}`} style={{ width: '0%' }}></div>
                </div>
              </div>
              <span className={`text-[12px] font-[300] animate-blink max-md:mt-1${isMastered ? ' text-[#78350f]' : ' text-[rgba(255,255,255,0.534)]'}`}>--% ke level selanjutnya</span>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1">
                <div className={`flex-1 h-[6px] rounded-full overflow-hidden${isMastered ? ' bg-[rgba(120,53,15,0.15)]' : ' bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(255,255,255,0.08)]'}`}>
                  <div
                    className={`h-full rounded-full transition-[width_0.3s_ease]${isMastered ? ' bg-[#78350f]' : ' bg-[#fcb319]'}`}
                    style={{ width: `${progressValue}%` }}
                  ></div>
                </div>
              </div>
              <span className={`text-[12px] font-[300] max-md:mt-1${isMastered ? ' text-[#78350f]' : ' text-[rgba(255,255,255,0.534)]'}`}>
                {isMastered ? `${progressValue}% Mastered` : `${progressValue}% ke level selanjutnya`}
              </span>
            </>
          )}
        </div>
      </div>
      {/* changed by Cursor: mastered message below progress bar, absolute so card size unchanged */}
      {isMastered && (
        <div className="text-[9px] font-semibold text-[#78350f] absolute bottom-2 max-md:bottom-[6px] left-4 max-md:left-[14px] z-[4]">
          <span className="bg-[rgba(255,255,255,0.5)] py-0.5 px-1.5 rounded-[4px] inline-block whitespace-nowrap">
            Congrats! You've mastered this {level && level.toLowerCase() === 'overall' ? 'course' : 'subcourse'}
          </span>
        </div>
      )}
    </div>
  );
};

export default SubcourseOverallCard;
